import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Wordmark } from "@/components/site/Brand";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { LogOut, Upload, Trash2, Loader2, Plus, ExternalLink } from "lucide-react";

const label = "font-sans text-xs tracking-widest uppercase text-olive-sage";
const input = "mt-1.5 w-full rounded-xl border border-beige bg-white px-4 py-3 font-sans text-sm outline-none focus:ring-2 focus:ring-olive-sage transition";
const btn = "inline-flex items-center gap-2 px-6 py-3 rounded-full bg-olive-deep text-cream font-sans text-sm hover:bg-blush-dusty transition-colors duration-300 disabled:opacity-60";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => { if (user === false) nav("/admin/login"); }, [user, nav]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-cream"><Loader2 className="w-6 h-6 animate-spin text-olive-sage" /></div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-cream">
      <header className="sticky top-0 z-40 bg-cream/85 backdrop-blur-xl border-b border-blush/30">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <Wordmark />
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noopener noreferrer" className="font-sans text-sm text-olive-sage hover:text-blush-dusty inline-flex items-center gap-1.5" data-testid="view-site-link">
              View site <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="logout-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-olive-sage/30 text-olive-deep font-sans text-sm hover:bg-white transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <h1 className="font-serif text-4xl text-olive-deep mb-2">Clinic Dashboard</h1>
        <p className="font-sans text-olive-sage mb-10">Manage everything patients see, and review their requests.</p>

        <Tabs defaultValue="content">
          <TabsList className="bg-blush-pale/60 rounded-full p-1 mb-8 flex-wrap h-auto">
            {["content", "photos", "testimonials", "appointments", "leads"].map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`tab-${t}`}
                className="rounded-full px-5 py-2 font-sans text-sm capitalize data-[state=active]:bg-olive-deep data-[state=active]:text-cream">
                {t === "leads" ? "Callbacks" : t}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="content"><ContentTab /></TabsContent>
          <TabsContent value="photos"><PhotosTab /></TabsContent>
          <TabsContent value="testimonials"><TestimonialsTab /></TabsContent>
          <TabsContent value="appointments"><ListTab endpoint="/appointments" type="appointments" /></TabsContent>
          <TabsContent value="leads"><ListTab endpoint="/leads" type="leads" /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Card({ children, className = "" }) {
  return <div className={`rounded-3xl bg-white border border-blush/30 p-8 shadow-[0_12px_40px_rgba(47,51,43,0.05)] ${className}`}>{children}</div>;
}

/* ---------------- Content ---------------- */
function ContentTab() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => { api.get("/content").then((r) => setC(r.data)); }, []);
  const set = (k) => (e) => setC((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/content", c); setC(data); toast.success("Content saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setC((p) => ({ ...p, doctor_photo: data.url }));
      toast.success("Photo uploaded — remember to Save");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  if (!c) return <Loader2 className="w-6 h-6 animate-spin text-olive-sage" />;

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-1">
        <p className={label}>Doctor photo</p>
        <div className="mt-4 arch overflow-hidden aspect-[3/4] bg-beige">
          {c.doctor_photo && <img src={fileUrl(c.doctor_photo)} alt="Doctor" className="w-full h-full object-cover" data-testid="admin-doctor-photo" />}
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="doctor-photo-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className={`${btn} mt-4 w-full justify-center`} data-testid="doctor-photo-upload">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {uploading ? "Uploading…" : "Change photo"}
        </button>
      </Card>

      <Card className="lg:col-span-2 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={label}>Doctor name</label><input className={input} value={c.doctor_name || ""} onChange={set("doctor_name")} data-testid="content-name" /></div>
          <div><label className={label}>Credentials</label><input className={input} value={c.credentials || ""} onChange={set("credentials")} data-testid="content-credentials" /></div>
        </div>
        <div><label className={label}>Bio</label>
          <textarea rows={6} className={input} value={c.bio || ""} onChange={set("bio")} data-testid="content-bio" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={label}>Opening text</label><input className={input} value={c.opening_text || ""} onChange={set("opening_text")} data-testid="content-opening" /></div>
          <div><label className={label}>Opening month</label><input className={input} value={c.opening_month || ""} onChange={set("opening_month")} data-testid="content-month" /></div>
        </div>
        <div><label className={label}>Calendly URL</label><input className={input} value={c.calendly_url || ""} onChange={set("calendly_url")} data-testid="content-calendly" /></div>
        <button onClick={save} disabled={saving} className={btn} data-testid="content-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {saving ? "Saving…" : "Save changes"}
        </button>
      </Card>
    </div>
  );
}

/* ---------------- Photos ---------------- */
function PhotosTab() {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const load = () => api.get("/photos").then((r) => setPhotos(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/photos", { storage_path: data.storage_path, url: data.url, caption: "" });
      toast.success("Photo added"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const del = async (id) => { await api.delete(`/photos/${id}`); toast.success("Removed"); load(); };

  return (
    <Card>
      <div className="flex items-center justify-between mb-6">
        <div><p className="font-serif text-2xl text-olive-deep">Clinic gallery</p><p className="font-sans text-sm text-olive-sage">Upload photos of your clinic space.</p></div>
        <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="gallery-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className={btn} data-testid="gallery-upload">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {uploading ? "Uploading…" : "Upload photo"}
        </button>
      </div>
      {photos.length === 0 ? (
        <p className="font-sans text-sm text-olive-sage py-10 text-center">No clinic photos yet.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {photos.map((p) => (
            <div key={p.id} className="group relative overflow-hidden rounded-2xl aspect-square" data-testid={`admin-photo-${p.id}`}>
              <img src={fileUrl(p.url)} alt="" className="w-full h-full object-cover" />
              <button onClick={() => del(p.id)} data-testid={`delete-photo-${p.id}`}
                className="absolute top-2 right-2 p-2 rounded-full bg-white/90 text-blush-dusty opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ---------------- Testimonials ---------------- */
function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", quote: "", rating: 5 });
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/testimonials").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const add = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/testimonials", { ...form, rating: Number(form.rating) }); toast.success("Testimonial added"); setForm({ name: "", location: "", quote: "", rating: 5 }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/testimonials/${id}`); toast.success("Removed"); load(); };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-1">
        <p className="font-serif text-2xl text-olive-deep mb-4">Add testimonial</p>
        <form onSubmit={add} className="space-y-4" data-testid="testimonial-form">
          <div><label className={label}>Name</label><input className={input} value={form.name} onChange={set("name")} required data-testid="testimonial-name" /></div>
          <div><label className={label}>Location</label><input className={input} value={form.location} onChange={set("location")} data-testid="testimonial-location" /></div>
          <div><label className={label}>Quote</label><textarea rows={4} className={input} value={form.quote} onChange={set("quote")} required data-testid="testimonial-quote" /></div>
          <div><label className={label}>Rating (1-5)</label><input type="number" min="1" max="5" className={input} value={form.rating} onChange={set("rating")} data-testid="testimonial-rating" /></div>
          <button type="submit" disabled={saving} className={btn} data-testid="testimonial-submit"><Plus className="w-4 h-4" /> Add</button>
        </form>
      </Card>
      <Card className="lg:col-span-2">
        <p className="font-serif text-2xl text-olive-deep mb-4">Published ({items.length})</p>
        {items.length === 0 ? <p className="font-sans text-sm text-olive-sage py-10 text-center">No testimonials yet.</p> : (
          <div className="space-y-4">
            {items.map((t) => (
              <div key={t.id} className="flex items-start justify-between gap-4 p-5 rounded-2xl border border-blush/30" data-testid={`admin-testimonial-${t.id}`}>
                <div>
                  <p className="font-serif text-lg text-olive-deep italic">"{t.quote}"</p>
                  <p className="font-sans text-sm text-olive-sage mt-2">{t.name}{t.location ? ` · ${t.location}` : ""} · {t.rating}★</p>
                </div>
                <button onClick={() => del(t.id)} data-testid={`delete-testimonial-${t.id}`} className="p-2 rounded-full hover:bg-blush-pale text-blush-dusty shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Leads / Appointments ---------------- */
function ListTab({ endpoint, type }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(endpoint).then((r) => setItems(r.data)).catch(() => setItems([])); }, [endpoint]);
  if (items === null) return <Loader2 className="w-6 h-6 animate-spin text-olive-sage" />;

  return (
    <Card>
      <p className="font-serif text-2xl text-olive-deep mb-6">{type === "appointments" ? "Appointment requests" : "Callback requests"} ({items.length})</p>
      {items.length === 0 ? <p className="font-sans text-sm text-olive-sage py-10 text-center">Nothing yet.</p> : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} className="p-5 rounded-2xl border border-blush/30 grid sm:grid-cols-4 gap-3 items-center" data-testid={`record-${it.id}`}>
              <div><p className="font-serif text-lg text-olive-deep">{it.name}</p><p className="font-sans text-xs text-olive-sage">{new Date(it.created_at).toLocaleString()}</p></div>
              <div className="font-sans text-sm text-olive-sage"><a href={`mailto:${it.email}`} className="hover:text-blush-dusty">{it.email}</a><br />{it.phone}</div>
              {type === "appointments" ? (
                <div><span className="inline-block px-3 py-1 rounded-full text-xs font-sans bg-blush-pale text-olive-deep capitalize">{it.consultation_type}</span>{it.preferred_date ? <p className="font-sans text-xs text-olive-sage mt-1">{it.preferred_date}</p> : null}</div>
              ) : <div />}
              <p className="font-sans text-sm text-olive-sage">{it.notes || it.message || "—"}</p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
